import { API_BASE_URL } from '../utils/apiUrl';

export { API_BASE_URL };

// Generic API call function
export const apiCall = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  const isFormData =
    typeof FormData !== 'undefined' && options.body instanceof FormData;
  const hasRequestBody = options.body !== undefined && options.body !== null;

  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        ...(hasRequestBody && !isFormData
          ? { 'Content-Type': 'application/json' }
          : {}),
        ...options.headers,
      },
    });

    let data;
    try {
      data = await response.json();
    } catch (jsonError) {
      throw new Error(`Server error: ${response.status} ${response.statusText}`);
    }

    // Accept both 200 and 201 as success
    if (!response.ok && response.status !== 201) {
      const errorMessage = data.error || data.message || `Request failed: ${response.status}`;
      throw new Error(errorMessage);
    }

    return data;
  } catch (error) {
    if (error.name !== 'AbortError') {
      console.error('API Call Error:', error);
    }
    throw error;
  }
};

// API Service Object
const api = {
  // Team Members
  getTeamMembers: () => apiCall('/team-members'),
  getTeamMember: (id) => apiCall(`/team-members/${id}`),

  // Tools - WITH CATEGORY ID, TYPE, AND SEARCH
  getTools: (params = {}) => {
    const { category_id = null, type = null, search = null, page = 1, limit = 6 } = params;
    let url = `/tools?page=${page}&limit=${limit}`;

    // Only add parameters if they have values
    if (category_id && category_id !== 'all') {
      url += `&category_id=${category_id}`;
    }
    if (type && type !== 'All') {
      url += `&type=${encodeURIComponent(type)}`;
    }
    if (search && search.trim()) {
      url += `&search=${encodeURIComponent(search)}`;
    }

    console.log('API URL:', url); // Debug
    return apiCall(url);
  },

  getTool: (id) => apiCall(`/tools/${id}`),

  // Tool Categories
  getToolCategories: () => apiCall('/tool-categories'),
  getToolCategory: (id) => apiCall(`/tool-categories/${id}`),

  // Events - WITH CATEGORY ID, SEARCH
  getEvents: (params = {}) => {
    const { featured = false, category_id = null, search = null, page = 1, limit = 6 } = params;
    let url = `/events?page=${page}&limit=${limit}`;
    if (featured) url += '&featured=1';
    if (category_id) url += `&category_id=${category_id}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    return apiCall(url);
  },
  getEvent: (id) => apiCall(`/events/${id}`),

  // Event Categories
  getEventCategories: () => apiCall('/event-categories'),
  getEventCategory: (id) => apiCall(`/event-categories/${id}`),

  // Blog Posts - WITH CATEGORY ID, SEARCH
  getBlogPosts: (params = {}) => {
    const { category_id = null, search = null, page = 1, limit = 6, skip = 0 } = params;
    let url = `/blog-posts?page=${page}&limit=${limit}&skip=${skip}`;
    if (category_id) url += `&category_id=${category_id}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    return apiCall(url);
  },
  getHeroBlogPost: () => apiCall('/blog-posts?hero=1'),
  getFeaturedBlogPosts: () => apiCall('/blog-posts?featured=1'),
  getBlogPost: (id) => apiCall(`/blog-posts/${id}`),

  // Blog Categories
  getBlogCategories: () => apiCall('/blog-categories'),
  getBlogCategory: (id) => apiCall(`/blog-categories/${id}`),

  // Experiments
  getExperiment: (id) => apiCall(`/experiments/${id}`),
  // Experiments - WITH STATUS FILTER
  getExperiments: (params = {}, options = {}) => {
    const { status = null } = params;
    let url = '/experiments';
    if (status) url += `?status=${encodeURIComponent(status)}`;
    return apiCall(url, options);
  },

  // Programs
  getPrograms: () => apiCall('/programs'),
  getProgram: (id) => apiCall(`/programs/${id}`),

  // Projects (Lab Stories)
  getProjects: () => apiCall('/projects'),
  getProject: (id) => apiCall(`/projects/${id}`),

  // Tab Contents (Homepage)
  getTabContents: () => apiCall('/tab-contents'),
  getTabContent: (tabName) => apiCall(`/tab-contents/${tabName}`),

  // Newsletter Subscription
  subscribeNewsletter: (email) =>
    apiCall('/newsletter', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  // Form Submissions
  submitForm: (formType, data) =>
    apiCall('/forms', {
      method: 'POST',
      body: JSON.stringify({ formType, data }),
    }),

  // Job Board APIs
  getJobCategories: () => apiCall('/job-categories'),

  getJobs: (params = {}) => {
    const { category_id = null, status = 'Open', page = 1, limit = 10 } = params;
    let url = `/job-postings?page=${page}&limit=${limit}`;
    if (category_id && category_id !== 'all') url += `&category_id=${category_id}`;
    if (status) url += `&status=${status}`;
    return apiCall(url);
  },

  getJob: (slug) => apiCall(`/job-postings/${slug}`),

  applyForJob: (formData) => {
    // Use FormData for file upload instead of JSON
    const url = `${API_BASE_URL}/job-applications`;

    return fetch(url, {
      method: 'POST',
      body: formData, // Don't set Content-Type, browser will set it with boundary
    })
      .then(response => response.json())
      .catch(error => {
        console.error('API Error:', error);
        throw error;
      });
  },

  // Site Settings
  getSettings: (format = 'grouped') => apiCall(`/settings?format=${format}`),
  getSetting: (key) => apiCall(`/settings/${key}`),
  updateSettings: (settings) =>
    apiCall('/settings', {
      method: 'PUT',
      body: JSON.stringify({ settings }),
    }),

  // Event Registrations
  registerForEvent: (eventData) =>
    apiCall('/event-registrations', {
      method: 'POST',
      body: JSON.stringify(eventData),
    }),

  getEventRegistrations: (eventId = null) => {
    const url = eventId ? `/event-registrations?event_id=${eventId}` : '/event-registrations';
    return apiCall(url);
  },

  getHeroScrollContent: () => apiCall('/hero-scroll-content'),

  cancelEventRegistration: (id) =>
    apiCall(`/event-registrations/${id}`, {
      method: 'DELETE',
    }),

  generateAudio: (postId) => apiCall(`/tts/generate/${postId}`, { method: 'POST' }),
  getAudio: (postId) => apiCall(`/tts/audio/${postId}`),
};

export default api;
